import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Card, Badge, Form, Spinner, Alert } from "react-bootstrap";
import DynamicTable from "../../components/DynamicTable";
import { fetchActivite, fetchClientTags, fetchClientVilles } from "./DataService";
import ClientDetailModal from "./ClientDetailModal";
import { TYPE_CLIENT_OPTIONS, VOLUME_BADGE } from "./constants";

const SEGMENTS = [
  { key: null,          label: "Tous",              bg: "#6b7280", emoji: "👥" },
  { key: "actif",       label: "Actifs réguliers",  bg: "#16a34a", emoji: "🟢" },
  { key: "occasionnel", label: "Occasionnels",       bg: "#ca8a04", emoji: "🟡" },
  { key: "veille",      label: "En veille",          bg: "#ea580c", emoji: "🟠" },
  { key: "inactif",     label: "Inactifs",           bg: "#dc2626", emoji: "🔴" },
];

const SEGMENT_BADGE = {
  actif:       { bg: "success",  label: "🟢 Actif régulier" },
  occasionnel: { bg: "warning",  label: "🟡 Occasionnel" },
  veille:      { bg: "orange",   label: "🟠 En veille" },
  inactif:     { bg: "danger",   label: "🔴 Inactif" },
};

function ClientsActiviteScreen() {
  const [data, setData]           = useState({ kpis: {}, clients: [] });
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [segment, setSegment]     = useState(null);
  const [search, setSearch]       = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [tagFilter, setTagFilter] = useState("");
  const [typeClientFilter, setTypeClientFilter] = useState("");
  const [villeFilter, setVilleFilter] = useState("");
  const [volumeFilter, setVolumeFilter] = useState("");
  const [availableTags, setAvailableTags] = useState([]);
  const [availableVilles, setAvailableVilles] = useState([]);

  useEffect(() => {
    fetchClientTags().then((d) => setAvailableTags(d.tags || [])).catch(() => {});
    fetchClientVilles().then((d) => setAvailableVilles(d.villes || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const reload = () => {
    setLoading(true);
    return fetchActivite(segment, searchDebounced || null, {
      tag: tagFilter || null,
      typeClient: typeClientFilter || null,
      ville: villeFilter || null,
      volume: volumeFilter || null,
    })
      .then((d) => {
        setData(d);
        // Garde la fiche ouverte à jour après une modification (tag/profil)
        if (selectedClient) {
          const updated = d.clients.find((c) => c.id === selectedClient.id);
          if (updated) setSelectedClient(updated);
        }
      })
      .catch(() => setError("Impossible de charger les données."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment, searchDebounced, tagFilter, typeClientFilter, villeFilter, volumeFilter]);

  const columns = useMemo(() => [
    {
      Header: "Entreprise",
      accessor: "RaisonSocial",
      Cell: ({ row: { original: r } }) => (
        <div>
          <div className="fw-semibold">{r.RaisonSocial || `${r.prenom} ${r.nom}`}</div>
          <div className="text-muted small">{r.email}</div>
        </div>
      ),
    },
    {
      Header: "Segment",
      accessor: "segment",
      Cell: ({ value }) => {
        const s = SEGMENT_BADGE[value] ?? { bg: "secondary", label: value };
        return <Badge bg={s.bg === "orange" ? "warning" : s.bg} text={s.bg === "warning" || s.bg === "orange" ? "dark" : undefined}>{s.label}</Badge>;
      },
    },
    {
      // Volume mesuré sur 90 jours ; tri sur le nombre de livraisons par mois.
      Header: "Volume",
      accessor: "livraisons_mois_90j",
      Cell: ({ row }) => {
        const r = row.original;
        const v = VOLUME_BADGE[r.volume_mesure];
        return v ? (
          <div>
            <Badge bg={v.bg}>{v.label}</Badge>
            <div className="text-muted small">{r.freq_semaine_90j} liv./sem.</div>
          </div>
        ) : <span className="text-muted small">— inactif</span>;
      },
    },
    {
      Header: "Commandes",
      accessor: "nb_commandes",
      Cell: ({ value }) => <span className="fw-bold">{value}</span>,
    },
    {
      Header: "CA total",
      accessor: "ca_total",
      Cell: ({ value }) => value ? `${parseFloat(value).toFixed(2)} €` : "—",
    },
    {
      Header: "Dernière commande",
      accessor: "derniere_commande",
      Cell: ({ value }) => value
        ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
        : <span className="text-danger">Jamais</span>,
    },
    {
      Header: "Cmd 30 jours",
      accessor: "cmd_30j",
      Cell: ({ value }) => (
        <span style={{ color: value >= 2 ? "#16a34a" : value === 1 ? "#ca8a04" : "#dc2626" }}>
          {value}
        </span>
      ),
    },
    {
      Header: "Inscrit le",
      accessor: "created_at",
      Cell: ({ value }) => new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }),
    },
    {
      Header: "Dernière connexion",
      accessor: "last_login_at",
      Cell: ({ value }) => value
        ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
        : <span className="text-muted">—</span>,
    },
    {
      Header: "Tags",
      accessor: "tags",
      Cell: ({ value }) => (
        <div className="d-flex flex-wrap gap-1">
          {(value || []).filter((t) => t.source === "manual").slice(0, 3).map((t, i) => (
            <Badge key={t.id ?? i} bg="info" style={{ fontSize: 11 }}>{t.tag}</Badge>
          ))}
        </div>
      ),
    },
    {
      Header: "",
      accessor: "id",
      Cell: ({ row: { original: r } }) => (
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => setSelectedClient(r)}
        >
          Fiche
        </button>
      ),
    },
  ], []);

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Activité clients entreprise</h4>
          <small className="text-muted">{data.kpis.total ?? 0} entreprises actives</small>
        </Col>
      </Row>

      {/* KPI cards */}
      <Row className="mb-4 g-3">
        {SEGMENTS.map((s) => (
          <Col key={s.key ?? "all"} xs={6} md={2}>
            <Card
              className="border-0 shadow-sm text-center h-100"
              style={{
                cursor: "pointer",
                borderBottom: segment === s.key ? `3px solid ${s.bg}` : "3px solid transparent",
                opacity: segment !== null && segment !== s.key ? 0.6 : 1,
              }}
              onClick={() => setSegment(segment === s.key ? null : s.key)}
            >
              <Card.Body className="py-3">
                <div style={{ fontSize: 28, fontWeight: 800, color: s.bg }}>
                  {s.key === null ? (data.kpis.total ?? 0) : (data.kpis[s.key] ?? 0)}
                </div>
                <div className="text-muted small mt-1">{s.emoji} {s.label}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Recherche + filtres */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-2">
            <Col md={3}>
              <Form.Control
                placeholder="Rechercher par nom, email, raison sociale..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Select size="sm" value={typeClientFilter} onChange={(e) => setTypeClientFilter(e.target.value)}>
                <option value="">Tous types de client</option>
                {TYPE_CLIENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select size="sm" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
                <option value="">Tous les tags</option>
                {availableTags.map((t) => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Col>
            <Col md={1}>
              <Form.Select size="sm" value={volumeFilter} onChange={(e) => setVolumeFilter(e.target.value)} title="Volume mesuré sur 90 jours">
                <option value="">Volume</option>
                {Object.entries(VOLUME_BADGE).map(([cle, v]) => <option key={cle} value={cle}>{v.label}</option>)}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select size="sm" value={villeFilter} onChange={(e) => setVilleFilter(e.target.value)}>
                <option value="">Toutes les villes</option>
                {availableVilles.map((v) => <option key={v} value={v}>{v}</option>)}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <DynamicTable
          columns={columns}
          data={data.clients}
          total={data.clients.length}
          last_page={1}
          nbr={1}
          setNbr={() => {}}
        />
      )}

      <ClientDetailModal
        client={selectedClient}
        show={!!selectedClient}
        onClose={() => setSelectedClient(null)}
        onUpdated={reload}
      />
    </Container>
  );
}

export default ClientsActiviteScreen;
